import { useState } from 'react';
import { LoginForm } from '../components/LoginForm';
import { MfaStep } from '../components/MfaStep';
import { login } from '../api/auth';
import type { AuthSession } from '../store/auth';
import { useAuthStore } from '../store/auth';
import { getApiClient } from '../api/factory';
import { useT } from '../i18n';

interface Props {
  onAuthenticated: () => Promise<void> | void;
  onEnrollmentRequired: (enrollmentSession: string) => Promise<void> | void;
}

type Step = { kind: 'password' } | { kind: 'mfa'; session: string };

// Двух-шаговый login. Шаг 1 — пароль (LoginForm). По результату:
//  - session  → сохраняем + onAuthenticated()
//  - mfa      → шаг 2 (MfaStep с выбором passkey / recovery)
//  - enroll   → router-уровень: onEnrollmentRequired (страница forced-enroll)
export function LoginView({ onAuthenticated, onEnrollmentRequired }: Props) {
  const t = useT();
  const [step, setStep] = useState<Step>({ kind: 'password' });

  async function handlePasswordSubmit(input: {
    email: string;
    password: string;
    tenantSlug: string;
  }) {
    const result = await login(getApiClient(), input);
    if (result.kind === 'session') {
      await acceptSession(result.session);
      return;
    }
    if (result.kind === 'mfa') {
      setStep({ kind: 'mfa', session: result.mfaSession });
      return;
    }
    // enrollment — это уже отдельная страница.
    await onEnrollmentRequired(result.enrollmentSession);
  }

  async function acceptSession(session: AuthSession) {
    useAuthStore.getState().setSession(session);
    await onAuthenticated();
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white p-6 rounded shadow-sm border border-slate-200 w-full max-w-sm">
        {step.kind === 'password' && (
          <>
            <h1 className="text-xl font-semibold mb-4">{t('auth.title')}</h1>
            <LoginForm onSubmit={handlePasswordSubmit} />
          </>
        )}
        {step.kind === 'mfa' && (
          <MfaStep mfaSession={step.session} onSuccess={acceptSession} />
        )}
      </div>
    </div>
  );
}
